import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Checkbox, Dialog, Input, Popup } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import { amountFormat } from "../../utils";
import {
  PayPasswordModal,
  EPayType,
  payTypeImage,
  payTypeLabel,
} from "../../components/Wallet";

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight} - 72px);
  overflow: auto;
  background: #fff;
`;

const Row = styled.div`
  display: flex;
  padding: 20px;
  border-bottom: 1px solid #ccc;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 700;
  min-width: 90px;
`;

const Tip = styled.div`
  font-size: 12px;
  color: #7f7f7f;
  line-height: 1.6;
  margin-bottom: 6px;
`;

const Footer = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  background: #fff;
  border-top: 1px solid #eee;
`;

const MyOrderCreate = () => {
  const { goBack, navigate } = useNavigation();
  const { orderStore, walletStore, configStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [quantities, setQuantities] = useState("");
  const [amount, setAmount] = useState("1.00");
  const [minTrading, setMinTrading] = useState("");
  const [checkPayment, setCheckPayment] = useState<number[]>([]);
  const [availableTypes, setAvailableTypes] = useState<number[]>([]);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);
  const { balance, is_id_verify, is_phone_verify } = walletStore;
  const { order_proposal_on, order_proposal_min, order_proposal_max } = configStore;

  useEffect(() => {
    (async () => {
      const data = await walletStore.loadPaymentList();
      const list = Array.from(data || walletStore.paymentList || []);
      const types = list
        .map((p: any) => p.pay_type)
        .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i)
        .sort((a: number, b: number) => a - b);
      setAvailableTypes(types);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const newAmount = amountFormat(amount);
    setAmount(newAmount);
    if (!Number(quantities)) {
      ToastError("请输入正确数量");
      return false;
    }
    if (/\./g.test(`${quantities}`)) {
      ToastError("出售数量仅支持输入整数");
      return false;
    }
    if (Number(quantities) < 100) {
      ToastError("出售数量不可小于100");
      return false;
    }
    if (Number(quantities) > Number(balance)) {
      ToastError("喜鹊币余额不足");
      return false;
    }
    if (!Number(newAmount)) {
      ToastError("请输入正确单价");
      return false;
    }
    if (order_proposal_on) {
      if (Number(newAmount) > order_proposal_max) {
        ToastError(`单价不可大于${order_proposal_max}`);
        return false;
      }
      if (Number(newAmount) < order_proposal_min) {
        ToastError(`单价不可小于${order_proposal_min}`);
        return false;
      }
    } else if (Number(newAmount) < 1) {
      ToastError("单价不可小于1");
      return false;
    }
    if (!Number(minTrading)) {
      ToastError("请输入正确最低起售");
      return false;
    }
    if (Number(minTrading) < 100) {
      ToastError("最低起售不可小于100");
      return false;
    }
    if (checkPayment.length <= 0) {
      ToastError("请选择收款方式");
      return false;
    }
    return true;
  };

  const submit = () => {
    if (!is_id_verify) {
      Dialog.confirm({
        title: "提示",
        content: "请先绑定实名认证,再进行下单",
        confirmText: "前往绑定",
        cancelText: "之后再说",
        onConfirm: () => navigate("Verified"),
      });
      return;
    }
    if (!is_phone_verify) {
      Dialog.confirm({
        title: "提示",
        content: "请先绑定手机,再进行下单",
        confirmText: "前往绑定",
        cancelText: "之后再说",
        onConfirm: () => {
          Dialog.clear();
          navigate("PhoneBinding");
        },
      });
      return;
    }
    if (!validateForm()) return;

    const payload = {
      quantities: Number(quantities),
      amount: Number(amountFormat(amount)),
      payment_methods: checkPayment,
      min_trading: Number(minTrading),
    };
    setLoading(true);
    orderStore.publicPendingOrderNew(
      payload,
      () => {
        walletStore.getWalletInfo();
        setLoading(false);
        ToastSuccess("挂单成功！");
        goBack(-1);
      },
      (error: any) => {
        ToastError(error?.message || "挂单失败");
        setLoading(false);
      }
    );
  };

  const pricePlaceholder = useMemo(() => {
    if (order_proposal_on) {
      return `建议单价区间 ${Number(order_proposal_min).toFixed(2)}-${Number(
        order_proposal_max
      ).toFixed(2)}(仅支持小数后两位)`;
    }
    return "单价不可小于1.00(仅支持小数后两位)";
  }, [order_proposal_on, order_proposal_min, order_proposal_max]);

  return (
    <PageWrap>
      <PageHeader title="挂单出售" />
      <ScrollArea>
        <Row>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {balance}
              <span style={{ fontWeight: 400, fontSize: 13 }}>喜鹊币</span>
            </div>
            <div style={{ color: "#aaa", fontSize: 13 }}>可售数量</div>
          </div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              1.00<span style={{ fontWeight: 400, fontSize: 13 }}>CNY</span>
            </div>
            <div style={{ color: "#aaa", fontSize: 13 }}>默认单价</div>
          </div>
        </Row>

        <Field>
          <Label>按数量出售</Label>
          <Input
            value={quantities}
            onChange={setQuantities}
            placeholder="最低出售喜鹊币100"
            style={{ "--text-align": "right" } as any}
          />
          <Button
            size="mini"
            fill="none"
            color="primary"
            onClick={() => setQuantities(`${balance}`)}
          >
            全部
          </Button>
        </Field>
        <Field>
          <Label>单价设定</Label>
          <Input
            value={amount}
            onChange={setAmount}
            onBlur={() => setAmount(amountFormat(amount))}
            placeholder={pricePlaceholder}
            style={{ "--text-align": "right" } as any}
          />
        </Field>
        <Field>
          <Label>最低起售</Label>
          <Input
            value={minTrading}
            onChange={setMinTrading}
            placeholder="最低不小于100"
            style={{ "--text-align": "right" } as any}
          />
        </Field>
        <Field style={{ cursor: "pointer" }} onClick={() => setPickerVisible(true)}>
          <Label>收款方式</Label>
          {checkPayment.length === 0 ? (
            <div style={{ color: "#999" }}>请选择收款方式 ›</div>
          ) : (
            <div style={{ display: "flex" }}>
              {checkPayment.map((t) => (
                <img
                  key={t}
                  src={payTypeImage(t)}
                  alt=""
                  width={18}
                  height={18}
                  style={{ marginLeft: 6 }}
                />
              ))}
            </div>
          )}
        </Field>

        <div style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>温馨提示</div>
          <Tip>1.实名认证：请使用平台实名信息一致的账户付款</Tip>
          <Tip>
            2.信用等级：成交单量，成交效率将影响您的信用等级，等级越高的用户推送越靠前
          </Tip>
          <Tip>3.交易：请实时留意订单状态</Tip>
          <Tip>4.出售：请务必确认收到款项后再进行打币</Tip>
          <Tip>
            5.订单完成：确认收款后，请尽快打币完成交易，订单出现异常请第一时间暂停交易并申诉；
          </Tip>
          <Tip>6、未按规定操作，平台不予承担责任。</Tip>
        </div>
      </ScrollArea>
      <Footer>
        <Button
          color="primary"
          block
          loading={loading}
          onClick={() => {
            if (!validateForm()) return;
            setPwdVisible(true);
          }}
        >
          立即挂单
        </Button>
      </Footer>

      <Popup
        visible={pickerVisible}
        onMaskClick={() => setPickerVisible(false)}
        bodyStyle={{ borderTopLeftRadius: 16, borderTopRightRadius: 16, paddingBottom: 28 }}
      >
        <div style={{ padding: 16, fontWeight: 700, textAlign: "center" }}>选择收款方式</div>
        {[EPayType.BANK, EPayType.ALIPAY, EPayType.WECHAT]
          .filter((t) => availableTypes.includes(t))
          .map((t) => (
            <div
              key={t}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                borderTop: "1px solid #eee",
              }}
              onClick={() => {
                setCheckPayment((prev) =>
                  prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].sort()
                );
              }}
            >
              <img src={payTypeImage(t)} alt="" width={20} height={20} />
              <span style={{ flex: 1, marginLeft: 10 }}>{payTypeLabel(t)}</span>
              <Checkbox checked={checkPayment.includes(t)} />
            </div>
          ))}
        <div style={{ padding: 16 }}>
          <Button color="primary" block onClick={() => setPickerVisible(false)}>
            确定
          </Button>
        </div>
      </Popup>

      <PayPasswordModal
        visible={pwdVisible}
        setVisible={setPwdVisible}
        callBack={(ok) => {
          if (ok) submit();
        }}
      />
    </PageWrap>
  );
};

export default observer(MyOrderCreate);
